/**
 * Product-owned Better Auth client hooks.
 *
 * These hooks keep the application-facing auth shape stable while the browser
 * talks directly to the same-origin Better Auth proxy exposed by `src/proxy`.
 */
import { useCallback, useMemo } from 'react'

import { authClient } from '@/features/auth/lib/auth-client'
import { useRouter } from '@/lib/navigation/router'

import { decodeCurrentAuthJwtClaims } from './better-auth-http'
import { clearBrowserSessionState } from './browser-session-boundary'

/** Refresh the cached JWT this long before its `exp` (clock-skew guard). */
const CLIENT_TOKEN_SKEW_MS = 60_000
/** Never serve a cached JWT longer than this (Worker default expiry is 15 min). */
const CLIENT_TOKEN_CAP_MS = 5 * 60_000

let clientTokenCache: { token: string; refreshAtMs: number } | null = null
/**
 * The one `/token` request in flight. On a hard load bootstrap, the parallel
 * prefetch and the entitlement hook all ask in the same tick; without this each
 * minted its own JWT.
 */
let inFlightToken: Promise<string | null> | null = null
/** Bumped on sign-out so a request that was in flight cannot cache the old session's JWT. */
let tokenGeneration = 0

function resetClientToken(): void {
  clientTokenCache = null
  inFlightToken = null
  tokenGeneration += 1
}

/** Test-only: clear the client bearer-token cache. */
export function _testResetClientTokenCache(): void {
  resetClientToken()
}

/**
 * The session ended or changed hands without `useAppSignOut` running (the session expired or was
 * revoked, another tab signed out or in). The cached bearer belongs to the previous user and must
 * not authenticate the next one's calls, so it goes with the rest of the browser session state.
 */
function endBrowserSession(): void {
  resetClientToken()
  clearBrowserSessionState()
}

let watchedUserId: string | null | undefined
let sessionWatchInstalled = false

function onSessionChange(value: { data?: unknown; isPending?: boolean } | null | undefined): void {
  if (!value || value.isPending) return
  const userId = (value.data as { user?: { id?: string } } | null | undefined)?.user?.id ?? null
  const previous = watchedUserId
  watchedUserId = userId
  if (previous === undefined || previous === userId) return
  if (previous === null) resetClientToken()
  else endBrowserSession()
}

/**
 * Watches Better Auth's session store, which every tab's sign-in, sign-out and expiry reaches, so
 * the boundary does not depend on the one code path that happens to call `useAppSignOut`.
 */
function ensureSessionWatch(): void {
  if (sessionWatchInstalled) return
  const session = (
    authClient as { $store?: { atoms?: { session?: { subscribe?: (listener: typeof onSessionChange) => unknown } } } }
  ).$store?.atoms?.session
  if (typeof session?.subscribe !== 'function') return
  sessionWatchInstalled = true
  session.subscribe(onSessionChange)
}

/**
 * The token endpoint gave no usable answer: the request never got a response
 * (offline, or the browser aborted it while the page navigated away), the
 * auth Worker failed (5xx, 429), or it answered 200 without a readable JWT.
 * It says nothing about the session, so callers
 * must treat it as a transport failure — never as a sign-out signal.
 */
export class WorkerTokenUnavailableError extends Error {
  constructor(options?: { cause?: unknown }) {
    super('Could not fetch the session token', options)
    this.name = 'WorkerTokenUnavailableError'
  }
}

function errorStatus(value: unknown): number | undefined {
  const status = (value as { status?: unknown } | null | undefined)?.status
  return typeof status === 'number' ? status : undefined
}

/**
 * The browser's Better Auth JWT for direct ProductData calls (goal 248).
 *
 * Resolves `null` only when the auth Worker ANSWERED that there is no session
 * (401) — the one outcome that may end the session. Any failure without that
 * answer throws `WorkerTokenUnavailableError` instead: a cancelled `/token`
 * request during navigation used to come back as `null`, read as a 401, and
 * sign the customer out (session-loss investigation, 2026-09-23).
 *
 * Exported as a plain function, not only through `useAppAuth`, because the
 * `queryFn` bodies that call `productData()` run outside React's render and
 * cannot use a hook. Both entry points share the module-level cache below, so
 * a page that mixes them still mints one token.
 */
export function getWorkerBearerToken(options: { fresh?: boolean } = {}): Promise<string | null> {
  // `fresh`: the API Worker refused the cached JWT (a 401 on a request that
  // carried it), so it is dropped and a new one minted. A mint already in
  // flight is reused: its answer is fresh by definition. The session itself
  // is untouched; only a `/token` 401 ends it.
  ensureSessionWatch()
  if (!options.fresh && clientTokenCache && clientTokenCache.refreshAtMs > Date.now()) {
    return Promise.resolve(clientTokenCache.token)
  }
  clientTokenCache = null
  if (inFlightToken) return inFlightToken

  const generation = tokenGeneration
  const request = mintWorkerBearerToken(generation).finally(() => {
    if (inFlightToken === request) inFlightToken = null
  })
  inFlightToken = request
  return request
}

/** `/token` answered 401: the session is gone. Skipped when a sign-out already ended it. */
function noSession(generation: number): null {
  if (generation === tokenGeneration) endBrowserSession()
  return null
}

async function mintWorkerBearerToken(generation: number): Promise<string | null> {
  const now = Date.now()
  let payload: unknown
  try {
    // better-fetch rejects only when no response arrived; a non-2xx resolves
    // as `{ data: null, error: { status } }`.
    // `cache: 'no-store'`: the token is per-session, per-minute state. Better
    // Auth sends it with no Cache-Control, and a back/forward load would
    // otherwise hand the boot an earlier visit's, by then expired, JWT
    // (NONRESIDENTTAX-FE). The Worker proxy marks the answer no-store too.
    payload = await authClient.$fetch('/token', { method: 'GET', cache: 'no-store' })
  } catch (cause) {
    // A thrown error carrying the server's 401 (a `throw: true` fetch) is still an answer.
    if (errorStatus(cause) === 401) return noSession(generation)
    throw new WorkerTokenUnavailableError({ cause })
  }

  const failure = (payload as { error?: unknown } | null)?.error
  if (failure) {
    if (errorStatus(failure) === 401) return noSession(generation)
    throw new WorkerTokenUnavailableError({ cause: failure })
  }

  // Better Auth answers 401 when there is no session, so a 200 means signed in.
  // A 200 without a usable JWT is a fault in the auth Worker or on the wire,
  // not a sign-out (of-3 C1).
  const record = payload as { data?: { token?: unknown } | null; token?: unknown } | null
  const token = record?.data?.token ?? record?.token
  if (typeof token !== 'string' || token.length === 0) {
    throw new WorkerTokenUnavailableError({ cause: new Error('Token endpoint answered without a token') })
  }

  const claims = decodeCurrentAuthJwtClaims(token)
  if (!claims) {
    throw new WorkerTokenUnavailableError({ cause: new Error('Token endpoint answered with an unreadable token') })
  }
  const { expMs } = claims
  if (expMs !== null) {
    const refreshAtMs = Math.min(expMs - CLIENT_TOKEN_SKEW_MS, now + CLIENT_TOKEN_CAP_MS)
    if (refreshAtMs > now && generation === tokenGeneration) {
      clientTokenCache = { token, refreshAtMs }
    }
  }
  return token
}

function splitName(name: string | null | undefined): { firstName: string | null; lastName: string | null } {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { firstName: null, lastName: null }
  return { firstName: parts[0], lastName: parts.length > 1 ? parts.slice(1).join(' ') : null }
}

function toIsoTimestamp(value: unknown): string | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString()
  if (typeof value === 'string') {
    const ms = Date.parse(value)
    return Number.isNaN(ms) ? null : new Date(ms).toISOString()
  }
  return null
}

type BetterAuthSessionData = ReturnType<typeof authClient.useSession> extends { data: infer D } ? D : never

function mapBetterAuthUser(data: BetterAuthSessionData): AppUser | null {
  const user = (
    data as {
      user?: {
        id: string
        email?: string | null
        name?: string | null
        image?: string | null
        createdAt?: unknown
      }
    } | null
  )?.user
  if (!user) return null
  const { firstName, lastName } = splitName(user.name)
  return {
    id: user.id,
    firstName,
    lastName,
    fullName: user.name?.trim() || null,
    imageUrl: user.image ?? '',
    emailAddresses: user.email ? [{ emailAddress: user.email }] : [],
    primaryEmailAddress: user.email ? { emailAddress: user.email } : null,
    externalAccounts: [],
    primaryPhoneNumber: null,
    createdAt: toIsoTimestamp(user.createdAt),
  }
}

interface AppAuth {
  userId: string | null
  sessionId: string | null
  isLoaded: boolean
  isSignedIn: boolean
  getWorkerBearerToken: () => Promise<string | null>
}

interface AppUser {
  id: string
  firstName: string | null
  lastName: string | null
  fullName: string | null
  imageUrl: string
  emailAddresses: { emailAddress: string }[]
  primaryEmailAddress: { emailAddress: string } | null
  externalAccounts: { provider: string }[]
  primaryPhoneNumber: { phoneNumber: string } | null
  /** Better Auth account creation time, ISO 8601; null when the session lacks it. */
  createdAt: string | null
}

export function useAppAuth(): AppAuth {
  ensureSessionWatch()
  const { data, isPending } = authClient.useSession()
  const session = (data as { session?: { id?: string | null }; user?: { id?: string } } | null) ?? null
  const getWorkerBearerTokenCallback = useCallback(async () => getWorkerBearerToken(), [])
  return {
    userId: session?.user?.id ?? null,
    sessionId: session?.session?.id ?? null,
    isLoaded: !isPending,
    isSignedIn: Boolean(session?.user?.id),
    getWorkerBearerToken: getWorkerBearerTokenCallback,
  }
}

interface AppUserResult {
  user: AppUser | null | undefined
  isLoaded: boolean
  isSignedIn: boolean | undefined
}

export function useAppUser(): AppUserResult {
  const { data, isPending } = authClient.useSession()
  // Callers put `user` in effect dependencies, so it must keep its identity
  // until the session itself changes.
  const user = useMemo(() => mapBetterAuthUser(data), [data])
  return { user, isLoaded: !isPending, isSignedIn: Boolean(user) }
}

/**
 * Signs out and lands on /sign-in. `next` (a same-site path) is where sign-in
 * returns to, e.g. the security page asking for a fresh sign-in.
 */
export function useAppSignOut(): (options?: { next?: string }) => Promise<void> {
  const router = useRouter()
  return useCallback(
    async (options?: { next?: string }) => {
      resetClientToken()
      try {
        await authClient.signOut()
      } catch {
        // The server-side revoke failed (offline, auth proxy unreachable). The
        // local session must end regardless: rethrowing here skipped the cleanup
        // and the redirect, and the customer sat on the loading screen with
        // their browser state intact (NONRESIDENT-EE). A cookie the server did
        // not clear is re-checked by the Worker's session guard on the next HTML
        // navigation.
      }
      clearBrowserSessionState()
      await router.push(options?.next ? `/sign-in?next=${encodeURIComponent(options.next)}` : '/sign-in')
    },
    [router],
  )
}

export function useAppRedirectToSignIn(): () => void {
  const router = useRouter()
  return useCallback(() => {
    void router.push('/sign-in')
  }, [router])
}

export function useAppSessionToken(): { session: { id: string } | null | undefined; isLoaded: boolean } {
  const { data, isPending } = authClient.useSession()
  const session = (data as { session?: { id: string } } | null)?.session ?? null
  return { session, isLoaded: !isPending }
}
